<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import useEventsBus from '@/utils/eventbus';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';

const { t } = useI18n();
const { gOn } = useEventsBus();
const visible = ref(false);
const states = reactive({
  mode: 'info',
  title: '',
  message: ''
});

// eslint-disable-next-line no-unused-vars
const resolveCallback = ref<((value: boolean) => void) | null>(null);


// eslint-disable-next-line no-unused-vars
const showConfirmation = (title: string, msg: string, resolve: (value: boolean) => void) => {
  states.mode = 'confirm';
  states.title = title;
  states.message = msg;
  visible.value = true;

  resolveCallback.value = resolve;
};

const showMessage = (mode: string, title: string, msg: string) => {
  states.mode = mode;
  states.title = title;
  states.message = msg;

  visible.value = true;
};

const targetModal = ref<HTMLElement | null>(null);
const shake = ref(false);

const sanitizedContent = computed(() => DOMPurify.sanitize(states?.message));

const iconName = computed(() => {
  if (states.mode === 'confirm') return 'CircleHelp';

  let result = 'Info';
  switch (states.mode) {
    case 'info':
      result = 'Info';
      break;
    case 'warning':
      result = 'ShieldAlert';
      break;
    case 'error':
      result = 'CircleX';
      break;
    default:
      result = 'Info';
      break;
  }
  return result;
});

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    close();
  } else if (event.key === 'Enter') {
    confirm();
  }
};


const confirm = () => {
  if (resolveCallback.value) {
    resolveCallback.value(true); // Resolve promise dengan true jika user konfirmasi
  }
  visible.value = false;
};

function cancel() {
  if (resolveCallback.value) {
    resolveCallback.value(false); // Resolve promise dengan false jika user membatalkan
  }
  visible.value = false;
}

// Mendengarkan event untuk menampilkan konfirmasi
gOn('showConfirmation', showConfirmation);
gOn('showMessage', showMessage);

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>
<template>
  <Teleport to="body">
    <Transition>
      <div
        v-if="visible"
        class="main-modal fixed w-full h-100 inset-0 z-[999] overflow-hidden flex justify-center items-center animated faster px-2"
      >
        <div
          class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center transition-opacity z-0"
        ></div>
        <div
          ref="targetModal"
          tabindex="0"
          :class="{ shake: shake }"
          class="bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg shadow-lg max-w-md w-full z-2 overflow-hidden outline-none"
        >
          <div class="w-full flex flex-col justify-center items-center p-6 gap-3">
            <neo-icon
              v-if="states.mode === 'confirm'"
              name="CircleHelp"
              :stroke-width="2"
              :size="64"
              class="mb-3 text-gray-600 dark:text-gray-300"
            />
            <neo-icon v-else :name="iconName" :stroke-width="2" :size="64" class="mb-3" />
            <div class="text-xl font-semibold uppercase">
              {{ t(states.title) }}
            </div>
            <div v-html="t(sanitizedContent)" class="text-inherit"></div>
          </div>
          <div
            v-if="states.mode !== 'confirm'"
            class="flex items-stretch justify-evenly overflow-hidden"
          >
            <button
              class="w-full h-12 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 active:bg-gray-300 dark:active:bg-gray-500 border-t border-zinc-300 dark:border-zinc-800 uppercase"
              tabindex="1"
              @click="cancel"
            >
              {{ t('button.ok') }}
            </button>
          </div>
          <div v-else class="flex items-stretch justify-evenly overflow-hidden">
            <button
              class="w-1/2 h-12 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 active:bg-gray-300 dark:active:bg-gray-500 border-r border-t border-zinc-300 dark:border-zinc-800 uppercase"
              tabindex="1"
              @click="confirm"
            >
              {{ t('button.yes') }}
            </button>
            <button
              class="w-1/2 h-12 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 active:bg-gray-300 dark:active:bg-gray-500 border-t border-zinc-300 dark:border-zinc-800 uppercase"
              tabindex="2"
              @click="cancel"
            >
              {{ t('button.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
<style scoped>
/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.shake {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-10px);
  }

  50% {
    transform: translateX(10px);
  }

  75% {
    transform: translateX(-10px);
  }

  100% {
    transform: translateX(0);
  }
}
</style>
